import React from 'react';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import {
    space, colors, fontSizes, radii
} from 'style/config';
import { Icon, Link } from 'components/ui';
import Pill from 'components/Pill';
import PillCouple from 'components/PillCouple';
import { REFINEMENT_TYPES } from '@ufe/shared/constants/catalog';
import { PICKUP, SAME_DAY, SHIP_TO_HOME } from 'constants/UpperFunnel';
import catalogUtils from 'utils/Catalog';
import Empty from 'constants/empty';
import happeningFiltersUtils from 'utils/happeningFilters';
import helpersUtils from 'utils/Helpers';
import { getOverriddenRefinementValue } from 'utils/FilterUtils';
import localeUtils from 'utils/LanguageLocale';
import EN from 'components/Catalog/Filters/locales/Filters_en_US.js';
import FR from 'components/Catalog/Filters/locales/Filters_fr_CA.js';

const { deferTaskExecution } = helpersUtils;
const { getLocalisedDataFromResource } = localeUtils;

const getText = getLocalisedDataFromResource(EN, FR);

class FilterPills extends BaseClass {
    constructor(props) {
        super(props);
    }

    getIconConfig = filterName => {
        const iconConfig = {};

        if (filterName === PICKUP) {
            iconConfig.iconName = 'store';
            iconConfig.iconSize = 12;
        }

        if (filterName === SAME_DAY) {
            iconConfig.iconName = 'bag';
            iconConfig.iconSize = 16;
        }

        if (filterName === SHIP_TO_HOME) {
            iconConfig.iconName = 'truck';
            iconConfig.iconSize = 16;
        }

        return iconConfig;
    };

    getPillContentWithIcon = (filterName, filterDisplayName) => {
        const { iconName, iconSize } = this.getIconConfig(filterName);
        const { showCompactPills } = this.props;

        return (
            <>
                <Icon
                    name={iconName || ''}
                    size={iconSize}
                    marginRight={showCompactPills ? 1 : 2}
                />
                {filterDisplayName}
            </>
        );
    };

    handlePillClick = refinement => () => {
        deferTaskExecution(() => {
            const { selectedFilters, selectFilters, toggleModal } = this.props;
            const filterHasSelectedValues = selectedFilters[refinement.displayName]?.length > 0;

            refinement?.property !== 'single'
                ? refinement.pillHandler
                    ? refinement.pillHandler()
                    : toggleModal(refinement.displayName)
                : selectFilters(
                    {
                        [refinement.displayName]: filterHasSelectedValues ? [] : [refinement.values[0].refinementValue]
                    },
                    true
                );
        });
    };

    handleToggleMegaModal = () => {
        const { toggleModal } = this.props;
        deferTaskExecution(() => {
            toggleModal('mega');
        });
    };

    buildValueChips = () => {
        const {
            showSimplifyFiltersOnPLP, selectedFilters, refinements, removeFilterValue, showCompactPills
        } = this.props;

        if (!showSimplifyFiltersOnPLP) {
            return [];
        }

        const refinementsByDisplayName = new Map(refinements.map(refinement => [refinement.displayName, refinement]));

        return Object.keys(selectedFilters).flatMap(filterName => {
            const refinement = refinementsByDisplayName.get(filterName);

            if (!refinement) {
                return [];
            }

            if (
                refinement.type === REFINEMENT_TYPES.CHECKBOXES_WITH_DROPDOWN ||
                refinement.type === REFINEMENT_TYPES.SORT ||
                refinement.type === REFINEMENT_TYPES.BEAUTY_PREFERENCES
            ) {
                return [];
            }

            return (selectedFilters[filterName] || [])
                .map(selectedValue => {
                    const currentValue = refinement.values.find(refinementValue => refinementValue.refinementValue === selectedValue);
                    const label = catalogUtils.isCustomRange(selectedValue)
                        ? catalogUtils.createCustomRangeDisplayName(selectedValue)
                        : currentValue?.refinementValueDisplayName;

                    if (!label) {
                        return null;
                    }

                    return (
                        <div
                            key={`chip-${filterName}-${selectedValue}`}
                            css={[styles.filter, showCompactPills && styles.compactPill]}
                        >
                            <div css={styles.valueChip}>
                                <span css={styles.valueChipLabel}>{label}</span>
                                <button
                                    type='button'
                                    onClick={() => removeFilterValue(filterName, selectedValue, true)}
                                    css={styles.valueChipRemove}
                                    aria-label={getText('removeFilter', [label])}
                                >
                                    <Icon
                                        name='x'
                                        size={space[2]}
                                    />
                                </button>
                            </div>
                        </div>
                    );
                })
                .filter(Boolean);
        });
    };

    render() {
        const {
            pillScrollRef,
            selectedFilters,
            refinements,
            activeItem,
            showSingleFilterPill,
            showCompactPills,
            showMegaFilterPill,
            showSimplifyFiltersOnPLP
        } = this.props;

        let isAnyFilterActive = false;
        let compactPillProps = {};

        if (showCompactPills) {
            compactPillProps = {
                minHeight: 32,
                paddingX: '10px',
                arrowSize: '.8em'
            };
        }

        const UpperFunnelPills = ({ refinement, valueFilter }) => {
            const filteredValues = valueFilter ? refinement.values.filter(valueFilter) : refinement.values;
            const components = filteredValues.map(value => {
                const overriddenValue = getOverriddenRefinementValue(value, getText);

                if (showSimplifyFiltersOnPLP && overriddenValue.isSelected) {
                    return (
                        <div
                            key={overriddenValue.filterKey}
                            css={[styles.filter, showCompactPills && styles.compactPill, styles.splitPill]}
                        >
                            <button
                                type='button'
                                onClick={e => {
                                    e.stopPropagation();
                                    overriddenValue.pillClick(e);
                                }}
                                css={styles.splitPillLeftButton}
                                aria-label={getText('removeFilter', [overriddenValue.refinementValueDisplayName])}
                            >
                                {this.getPillContentWithIcon(overriddenValue.filterKey, overriddenValue.refinementValueDisplayName)}
                            </button>
                            <button
                                type='button'
                                onClick={overriddenValue.labelClick}
                                css={styles.splitPillRightButton}
                                aria-label={getText('changeFilterValue', [
                                    overriddenValue.refinementValueDisplayName,
                                    overriddenValue.refinementValueSpecificDisplayName || ''
                                ])}
                            >
                                {overriddenValue.refinementValueSpecificDisplayName?.toLowerCase()}
                                <Icon
                                    name='caretDown'
                                    size={space[3]}
                                />
                            </button>
                        </div>
                    );
                }

                if (showSingleFilterPill) {
                    return (
                        <div
                            key={overriddenValue.filterKey}
                            css={[styles.filter, showCompactPills && styles.compactPill]}
                        >
                            <Pill
                                onClick={overriddenValue.pillClick}
                                isActive={overriddenValue.isSelected}
                                fontSize='sm'
                                children={this.getPillContentWithIcon(overriddenValue.filterKey, overriddenValue.refinementValueDisplayName)}
                                {...compactPillProps}
                            />
                        </div>
                    );
                }

                return (
                    <PillCouple
                        css={styles.filter}
                        key={overriddenValue.filterKey}
                    >
                        <Pill
                            onClick={overriddenValue.pillClick}
                            isActive={overriddenValue.isSelected}
                            fontSize='sm'
                            children={this.getPillContentWithIcon(overriddenValue.filterKey, overriddenValue.refinementValueDisplayName)}
                        />
                        {overriddenValue.describedByComp}
                        <Pill
                            aria-describedby={overriddenValue.describedById}
                            onClick={overriddenValue.labelClick}
                            useActiveArrow={false}
                            fontSize='sm'
                            hasArrow={true}
                            children={overriddenValue.refinementValueSpecificDisplayName}
                        />
                    </PillCouple>
                );
            });

            return components;
        };

        const { onClearAllFilters } = this.props;
        const hasAnyActiveFilter = Object.values(selectedFilters || Empty.Object).some(
            selectedValues => Array.isArray(selectedValues) && selectedValues.length > 0
        );

        const valueChips = this.buildValueChips();

        const pills = refinements.map(refinement => {
            const isRadiosCustom = refinement.subType === REFINEMENT_TYPES.RADIOS_CUSTOM;
            const isCheckboxCustom = refinement.subType === REFINEMENT_TYPES.CHECKBOXES_CUSTOM;
            const filterHasSelectedValues = selectedFilters[refinement.displayName]?.length > 0;
            const isSelectedCheckboxOrNonDefault =
                filterHasSelectedValues && happeningFiltersUtils.getIsValueCheckboxOrNonDefault(selectedFilters[refinement.displayName], refinement);

            if (!isAnyFilterActive && isSelectedCheckboxOrNonDefault) {
                isAnyFilterActive = true;
            }

            const showPill = refinement.type !== REFINEMENT_TYPES.CHECKBOXES_WITH_DROPDOWN;
            const customCheckBoxesFilterName = Object.values(refinements).find(x => x.subType === REFINEMENT_TYPES.CHECKBOXES_CUSTOM)?.displayName;
            const selectedCustomValues = selectedFilters[customCheckBoxesFilterName];
            let label;

            if (refinement.type === REFINEMENT_TYPES.SORT && !isRadiosCustom) {
                label = `${refinement.displayName}: ${catalogUtils.getSelectedOrDefaultSortOption(refinement.values).refinementValueDisplayName}`;

                if (showCompactPills) {
                    label = refinement.displayName;
                }
            } else if (isRadiosCustom) {
                label = `${refinement.displayName}: ${
                    happeningFiltersUtils.getSelectedOrDefaultRefinement(
                        refinements,
                        refinement.displayName,
                        selectedFilters[refinement.displayName][0]
                    ).refinementValueDisplayName
                }`;
            } else if (isCheckboxCustom) {
                label = selectedCustomValues?.length > 0 ? `${refinement.subTitle} (${selectedCustomValues.length})` : refinement.subTitle;
            } else {
                label = refinement.displayName;
            }

            return {
                type: refinement.type,
                element: (
                    <React.Fragment key={`pill-wrapper-${refinement.displayName}`}>
                        {showPill && (
                            <div
                                key={`pill-${refinement.displayName}`}
                                css={[styles.filter, showCompactPills && styles.compactPill]}
                            >
                                <Pill
                                    onClick={this.handlePillClick(refinement)}
                                    isActive={activeItem === refinement.displayName || isSelectedCheckboxOrNonDefault}
                                    useActiveArrow={false}
                                    fontSize='sm'
                                    hasArrow={refinement?.property !== 'single'}
                                    children={label}
                                    {...compactPillProps}
                                />
                            </div>
                        )}
                        {!showPill && <UpperFunnelPills refinement={refinement} />}
                    </React.Fragment>
                )
            };
        });

        const sortPills = pills.filter(pill => pill.type === REFINEMENT_TYPES.SORT).map(pill => pill.element);
        const otherPills = pills
            .filter(pill => pill.type !== REFINEMENT_TYPES.SORT && pill.type !== REFINEMENT_TYPES.CHECKBOXES_WITH_DROPDOWN)
            .map(pill => pill.element);

        const fulfillmentRefinements = refinements.filter(refinement => refinement.type === REFINEMENT_TYPES.CHECKBOXES_WITH_DROPDOWN);
        const selectedFulfillmentPills = fulfillmentRefinements.map(refinement => (
            <UpperFunnelPills
                key={`sel-fulfillment-${refinement.displayName}`}
                refinement={refinement}
                valueFilter={refinementValue => refinementValue.isSelected}
            />
        ));
        const unselectedFulfillmentPills = fulfillmentRefinements.map(refinement => (
            <UpperFunnelPills
                key={`unsel-fulfillment-${refinement.displayName}`}
                refinement={refinement}
                valueFilter={refinementValue => !refinementValue.isSelected}
            />
        ));

        return (
            <div
                ref={pillScrollRef}
                css={[styles.filtersWrap, this.props.filterShadow]}
            >
                {showMegaFilterPill && (
                    <div css={styles.filter}>
                        <Pill
                            onClick={this.handleToggleMegaModal}
                            isActive={activeItem === 'mega' || isAnyFilterActive}
                            paddingX={null}
                            width={compactPillProps.minHeight || 36}
                            {...compactPillProps}
                        >
                            <Icon
                                name='filter'
                                size={showCompactPills ? 14 : 17}
                            />
                        </Pill>
                    </div>
                )}
                {showSimplifyFiltersOnPLP ? (
                    <>
                        {selectedFulfillmentPills}
                        {valueChips}
                        {hasAnyActiveFilter && onClearAllFilters && (
                            <span css={styles.clearAllWrap}>
                                <Link
                                    data-at={Sephora.debug.dataAt('clear_all_link')}
                                    onClick={onClearAllFilters}
                                    color='blue'
                                    fontSize='sm'
                                >
                                    {getText('clearAll')}
                                </Link>
                            </span>
                        )}
                        {sortPills}
                        {unselectedFulfillmentPills}
                        {otherPills}
                    </>
                ) : (
                    pills.map(pill => pill.element)
                )}
            </div>
        );
    }
}

const styles = {
    filtersWrap: {
        whiteSpace: 'nowrap',
        marginLeft: -space.container,
        marginRight: -space.container,
        paddingLeft: space.container,
        paddingRight: space.container,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        // prevent cut off of focus outline
        paddingTop: space[1],
        paddingBottom: space[1],
        marginTop: -space[1],
        marginBottom: -space[1]
    },
    filter: {
        display: 'inline-block',
        verticalAlign: 'top',
        textTransform: 'capitalize',
        ':not(:first-child)': {
            marginLeft: space[2]
        }
    },
    compactPill: {
        ':not(:first-child)': {
            marginLeft: space[1]
        }
    },
    splitPill: {
        display: 'inline-flex',
        alignItems: 'stretch',
        background: colors.black,
        borderRadius: radii.full,
        height: space[6],
        overflow: 'hidden',
        fontSize: fontSizes.sm
    },
    splitPillLeftButton: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: `0 ${space[2]}px`,
        background: 'transparent',
        border: 0,
        color: colors.white,
        fontFamily: 'inherit',
        fontSize: 'inherit',
        cursor: 'pointer',
        svg: {
            marginLeft: space[1]
        },
        '&:focus-visible': {
            outline: `2px solid ${colors.white}`,
            outlineOffset: -2
        }
    },
    splitPillRightButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: space[1],
        padding: `0 ${space[2]}px`,
        background: colors.lightGray,
        color: colors.black,
        borderTop: `1px solid ${colors.black}`,
        borderRight: `1px solid ${colors.black}`,
        borderBottom: `1px solid ${colors.black}`,
        borderLeft: 0,
        borderTopRightRadius: radii.full,
        borderBottomRightRadius: radii.full,
        fontFamily: 'inherit',
        fontSize: 'inherit',
        cursor: 'pointer',
        textTransform: 'capitalize',
        '&:focus-visible': {
            outline: `2px solid ${colors.blue}`,
            outlineOffset: -2
        }
    },
    valueChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: space[1],
        height: space[6],
        paddingLeft: 10,
        paddingRight: space[1],
        background: colors.black,
        color: colors.white,
        borderRadius: radii.full,
        fontSize: fontSizes.sm
    },
    valueChipLabel: {
        display: 'inline-block'
    },
    valueChipRemove: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 0,
        paddingTop: space[1],
        paddingBottom: space[1],
        paddingLeft: 2,
        paddingRight: space[2],
        cursor: 'pointer',
        color: colors.white,
        svg: {
            fill: colors.white
        },
        '&:focus-visible': {
            outline: `2px solid ${colors.white}`,
            outlineOffset: -1
        }
    },
    clearAllWrap: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: space[6],
        marginLeft: space[2],
        marginRight: space[1],
        verticalAlign: 'top'
    }
};

export default wrapComponent(FilterPills, 'FilterPills');
